// ===================================
// UPDATED DASHBOARD WITH PROPER BACKEND INTEGRATION
// src/pages/Dashboard.tsx
// ===================================
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { documentApi, chatApi, searchApi, adminApi } from "../services/api";
import FloatingChat from "./FloatingChat";
import StatsCard from "../components/StatsCard";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  FileText,
  Upload,
  MessageSquare,
  Search,
  Users,
  Database,
  Zap,
  CheckCircle,
  AlertCircle,
  Clock,
  BarChart3,
  RefreshCw,
  Settings as SettingsIcon,
} from "lucide-react";
import { Document, ChatSession, SearchStats, AdminStats } from "../types";
import Header from "../components/Header";
import { notDeepEqual } from "assert";
import { notifyError, notifySuccess } from "../utils/Toastify";

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const {
    hideChatSidebar,
    setHideChatSidebar,
    darkMode,
    setDarkMode,
    notifications,
    setNotifications,
  } = useSettings();

  // State
  const [documents, setDocuments] = useState<Document[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number;
  }>({});
  const [showFloatingChat, setShowFloatingChat] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();

    // Auto-refresh every 30 seconds for processing status
    const interval = setInterval(() => {
      if (!isLoading) {
        loadDocuments();
        loadSearchStats();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshTrigger]);

  useEffect(() => {}, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load all data in parallel
      await Promise.all([
        loadDocuments(),
        loadSessions(),
        loadAdminStats(),
        loadSearchStats(),
      ]);
    } catch (error) {
      setError("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await documentApi.list({
        limit: 50,
        sort_by: "created_at",
        sort_order: "desc",
      });

      // Handle different response structures
      let documentsData: Document[] = [];
      if (response.data?.documents && Array.isArray(response.data.documents)) {
        documentsData = response.data.documents;
      } else if (Array.isArray(response.data)) {
        documentsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        documentsData = response.data.data;
      }

      setDocuments(documentsData);
    } catch (error) {
      setDocuments([]);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await chatApi.getSessions({ limit: 20 });

      // Handle different response structures
      let sessionsData: ChatSession[] = [];
      if (response.data?.sessions && Array.isArray(response.data.sessions)) {
        sessionsData = response.data.sessions;
      } else if (Array.isArray(response.data)) {
        sessionsData = response.data;
      }

      setSessions(sessionsData);
    } catch (error) {
      setSessions([]);
    }
  };

  const loadAdminStats = async () => {
    if (user?.is_admin) {
      try {
        const response = await adminApi.dashboard();
        setAdminStats(response.data);
      } catch (error) {
        setAdminStats(null);
      }
    }
  };

  const loadSearchStats = async () => {
    try {
      const response = await searchApi.stats();

      // Handle different response structures
      let statsData: SearchStats | null = null;
      if (response.data?.search_analytics) {
        statsData = response.data.search_analytics;
      } else if (response.data?.data) {
        statsData = response.data.data;
      } else {
        statsData = response.data;
      }

      setSearchStats(statsData);
    } catch (error) {
      setSearchStats(null);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadId = Date.now().toString() + Math.random().toString();
    setUploadProgress((prev) => ({ ...prev, [uploadId]: 0 }));

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("access_level", "business");
      formData.append("enable_text_extraction", "true");

      const response = await documentApi.upload(formData);

      // Remove progress indicator
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[uploadId];
        return newProgress;
      });
      notifySuccess(`${file.name} uploaded successfully! Processing started.`);

      // Reload documents and trigger refresh
      await loadDocuments();
      setRefreshTrigger(Date.now());
    } catch (error: any) {
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[uploadId];
        return newProgress;
      });
      notifyError(
        error.response?.data?.error?.message || `Failed to upload ${file.name}`
      );
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "processing":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-[#64748b]" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center bg-[#020617]">
  //       <LoadingSpinner size="lg" message="Loading dashboard..." />
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white  mb-2">
            Error Loading Dashboard
          </h2>
          <p className="text-[#64748b]  mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              loadData();
            }}
            className="bg-[#8b3dff] text-white px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a]">
      {/* Header */}
      <Header
        title=" Enterprise RAG Dashboard"
        subtitle=" Phase 3: RAG-Powered Document Intelligence"
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Quick Stats */}
          {adminStats?.overview && (
            <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-6">
              <StatsCard
                title="Documents"
                value={adminStats.overview.total_documents}
                icon={FileText}
                color="blue"
                subtitle="Total uploaded"
              />
              <StatsCard
                title="Processed"
                value={adminStats.overview.processed_documents}
                icon={CheckCircle}
                color="green"
                subtitle="Successfully processed"
              />
              <StatsCard
                title="With Text"
                value={adminStats.overview.documents_with_text}
                icon={Database}
                color="purple"
                subtitle="Text extracted"
              />
              <StatsCard
                title="Success Rate"
                value={`${adminStats.overview.text_extraction_rate}%`}
                icon={BarChart3}
                color="orange"
                subtitle="Processing success"
              />
              <StatsCard
                title="Storage"
                value={`${adminStats.overview.total_storage_mb}MB`}
                icon={Database}
                color="indigo"
                subtitle="Total storage used"
              />
              <StatsCard
                title="Chat Sessions"
                value={adminStats.overview.total_sessions}
                icon={MessageSquare}
                color="green"
                subtitle="Total conversations"
              />
            </div>
          )}
          {/* Admin Statistics */}
          {user?.is_admin && adminStats?.text_extraction && (
            <div className="mt-8 bg-[#1e293b] mb-8 rounded-lg shadow-sm border border-[#334155] p-6">
              <h3 className="text-lg font-semibold text-white  mb-6">
                📊 Admin Analytics
              </h3>

              <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {adminStats.text_extraction.extraction_success_rate}%
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Extraction Success
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-[#8b3dff]">
                    {adminStats.text_extraction.avg_text_length.toLocaleString()}
                  </div>
                  <div className="text-sm text-[#64748b]">Avg Text Length</div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {Math.round(
                      adminStats.text_extraction.avg_processing_time / 1000
                    )}
                    s
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Avg Processing Time
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {Math.round(
                      adminStats.text_extraction.total_text_length / 1024 / 1024
                    )}
                    MB
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Total Text Extracted
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RAG System Status */}
          {searchStats && (
            <div className="mb-8 bg-[#1e293b] border-[#334155] border rounded-lg shadow-sm    p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white  flex items-center">
                  🤖 RAG System Status
                </h3>
                <button
                  onClick={loadSearchStats}
                  className="text-sm text-[#8b3dff] hover:text-[#8b3dff] flex items-center space-x-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {searchStats.chunks_with_embeddings || 0}
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Searchable Chunks
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#8b3dff]">
                    {searchStats.total_documents || 0}
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Indexed Documents
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">
                    {searchStats.avg_embedding_dimensions || 0}
                  </div>
                  <div className="text-sm text-[#64748b]">
                    Vector Dimensions
                  </div>
                </div>
                <div className="text-center">
                  <div
                    className={`text-3xl font-bold ${
                      searchStats.chunks_with_embeddings > 0
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {searchStats.chunks_with_embeddings > 0 ? "✅" : "⏳"}
                  </div>
                  <div className="text-sm text-[#64748b]">
                    {searchStats.chunks_with_embeddings > 0
                      ? "Ready"
                      : "Processing"}
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => (window.location.href = "/search")}
                  disabled={searchStats.chunks_with_embeddings === 0}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <Search className="w-4 h-4" />
                  <span>Search Documents</span>
                </button>
                <button
                  onClick={() => (window.location.href = "/chat")}
                  className="bg-[#8b3dff] text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>RAG Chat</span>
                </button>
              </div>
            </div>
          )}

          {/* Documents and Sessions Grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Documents Section */}
            <div className="bg-[#1e293b] rounded-lg shadow-sm border border-[#334155]">
              <div className="px-6 py-4 border-b border-[#334155]">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-white ">
                    Documents ({documents.length})
                  </h3>
                  <button
                    onClick={loadDocuments}
                    className="text-sm text-[#8b3dff] hover:text-blue-800 flex items-center space-x-1"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Upload Section */}
                <div className="mb-6">
                  <label className="block">
                    <input
                      type="file"
                      className="block w-full text-sm text-[#64748b] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#8b3dff] hover:file:bg-blue-100 file:cursor-pointer cursor-pointer"
                      onChange={handleFileUpload}
                      accept=".pdf,.csv,.txt,.docx,.md"
                    />
                  </label>

                  {/* Upload Progress */}
                  {Object.entries(uploadProgress).map(([id, progress]) => (
                    <div key={id} className="mt-3">
                      <div className="flex justify-between text-sm text-[#64748b]  mb-1">
                        <span>Uploading...</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#8b3dff] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Documents List */}
                <div className="space-y-3 max-h-80 overflow-y-auto ">
                  {documents.length > 0 ? (
                    documents.map((doc) => (
                      <div
                        key={doc.id}
                        className=" bg-[#020617] border border-[#334155] rounded-lg p-4  transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              {getStatusIcon(doc.processing_status)}
                              <div className="text-sm font-medium text-white  truncate">
                                {doc.original_filename}
                              </div>
                            </div>

                            <div className="text-xs text-[#64748b] space-y-1">
                              <div>
                                {doc.file_type.toUpperCase()} •{" "}
                                {formatFileSize(doc.file_size)}
                              </div>

                              {doc.has_extracted_text && (
                                <div className="flex items-center space-x-2 text-green-600">
                                  <span>📄</span>
                                  <span>
                                    {doc.extraction_word_count.toLocaleString()}{" "}
                                    words extracted
                                  </span>
                                </div>
                              )}

                              {doc.processing_status === "processing" && (
                                <div className="flex items-center space-x-2 text-[#8b3dff]">
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  <span>Processing...</span>
                                </div>
                              )}

                              <div>
                                Uploaded:{" "}
                                {new Date(doc.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>

                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                              doc.processing_status
                            )}`}
                          >
                            {doc.processing_status}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-[#64748b] text-sm mb-2">
                        No documents uploaded yet
                      </p>
                      <p className="text-gray-400 text-xs">
                        Upload your first document to get started!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Chat Sessions Section */}
            <div className="bg-[#1e293b] rounded-lg shadow-sm border border-[#334155] ">
              <div className="px-6 py-4 border-b border-[#334155] ">
                <h3 className="text-lg font-semibold text-white ">
                  Recent Chat Sessions ({sessions.length})
                </h3>
              </div>

              <div className="p-6">
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {sessions.length > 0 ? (
                    sessions.map((session) => (
                      <div
                        key={session.id}
                        className="border border-[#334155] rounded-lg p-3  transition-colors"
                      >
                        <div className="text-sm font-medium text-white  truncate mb-1">
                          {session.title || "Untitled Chat"}
                        </div>
                        <div className="text-xs text-[#64748b] flex items-center justify-between">
                          <span>{session.message_count} messages</span>
                          <span>
                            {new Date(session.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                        {session.metadata?.type && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              {session.metadata.type === "rag_chat"
                                ? "🧠"
                                : "💬"}{" "}
                              {session.metadata.type.replace("_", " ")}
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-[#64748b] text-sm mb-2">
                        No chat sessions yet
                      </p>
                      <p className="text-gray-400 text-xs">
                        Start your first conversation!
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => (window.location.href = "/chat")}
                  className="mt-4 w-full bg-[#8b3dff] text-white py-3 px-4 rounded-lg transition-colors font-medium"
                >
                  Start New Chat
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Floating Chat Widget */}
      {showFloatingChat && (
        <FloatingChat onClose={() => setShowFloatingChat(false)} />
      )}
    </div>
  );
};

export default Dashboard;